import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import AdminLayout from '../../components/Admin/AdminLayout'
import AdminModal from '../../components/Admin/AdminModal'
import { useConfirm } from '../../contexts/ConfirmContext'

function StatCard({ icon, cls, label, value, sub }) {
  return (
    <div className="admin-stat-card flex">
      <span className={`admin-stat-icon ${cls}`}>{icon}</span>
      <div className="admin-stat-info">
        <div className="label">{label}</div>
        <div className="admin-stat-value sm">{value}</div>
        {sub != null && sub !== '' && <div className="sub">{sub}</div>}
      </div>
    </div>
  )
}

function typeKey(type) {
  const t = String(type || '').toLowerCase()
  if (t === 'serviceprovider') return 'advisor'
  return t || 'gp'
}

function verifiedLabel(status) {
  if (status === 'verified') return '\u2713 Fully Verified'
  if (status === 'unverified') return '\u2717 Unverified'
  return '\u23f3 Pending Review'
}

export default function AdminCompany() {
  const confirm = useConfirm()
  const [stats, setStats] = useState(null)
  const [companies, setCompanies] = useState([])
  const [typeFilter, setTypeFilter] = useState('all')
  const [verifiedFilter, setVerifiedFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [detailCompany, setDetailCompany] = useState(null)
  const [busyId, setBusyId] = useState(null)

  const load = () => {
    axios.get('/api/admin/companies')
      .then((r) => {
        setStats(r.data.stats)
        setCompanies(r.data.companies || [])
      })
      .catch(() => toast.error('\u52a0\u8f7d\u5931\u8d25'))
  }

  useEffect(() => {
    load()
  }, [])

  const suspendCompany = async (company) => {
    const name = company.full_name || company.short_name
    const ok = await confirm({
      title: 'Suspend company?',
      message: `Suspend all active users at ${name}?`,
      confirmText: 'Suspend',
      variant: 'danger',
    })
    if (!ok) return
    setBusyId(company.id)
    try {
      const res = await axios.post(`/api/admin/companies/${company.id}/suspend`, {
        reason: 'terms',
        note: 'Suspended from Company Management',
      })
      toast.success(res.data?.message || 'Company suspended')
      load()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Suspend failed')
    } finally {
      setBusyId(null)
    }
  }

  const unsuspendCompany = async (company) => {
    const name = company.full_name || company.short_name
    const ok = await confirm({
      title: 'Restore company?',
      message: `Restore ${name} and unsuspend its users?`,
      confirmText: 'Restore',
      variant: 'warning',
    })
    if (!ok) return
    setBusyId(company.id)
    try {
      const res = await axios.post(`/api/admin/companies/${company.id}/unsuspend`)
      toast.success(res.data?.message || 'Company restored')
      load()
      setDetailCompany(null)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Unsuspend failed')
    } finally {
      setBusyId(null)
    }
  }

  const filtered = useMemo(() => companies.filter((c) => {
    const type = typeKey(c.primary_type || c.company_type)
    if (typeFilter !== 'all' && type !== typeFilter.toLowerCase()) return false
    if (verifiedFilter !== 'all' && c.verification_status !== verifiedFilter) return false
    if (statusFilter === 'active' && c.status === 'suspended') return false
    if (statusFilter === 'suspended' && c.status !== 'suspended') return false
    if (search) {
      const q = search.toLowerCase()
      if (!c.full_name?.toLowerCase().includes(q) && !c.email_domain?.toLowerCase().includes(q)) return false
    }
    return true
  }), [companies, typeFilter, verifiedFilter, statusFilter, search])

  return (
    <AdminLayout title="Company Management">
      <div className="admin-stats-row cols-5">
        <StatCard icon={'\ud83c\udfe2'} cls="blue" label="Total Companies" value={stats?.total ?? 0} sub={`+${stats?.newThisMonth ?? 0} this month`} />
        <StatCard icon={'\ud83c\udfdb\ufe0f'} cls="orange" label="GP (Fund Managers)" value={stats?.gp ?? 0} sub={`${stats?.gpPct ?? 0}% of total`} />
        <StatCard icon={'\ud83d\udcbc'} cls="green" label="LP (Institutions)" value={stats?.lp ?? 0} sub={`${stats?.lpPct ?? 0}% of total`} />
        <StatCard icon={'\ud83d\ude80'} cls="gray" label="Startups" value={stats?.startup ?? 0} sub={`${stats?.startupPct ?? 0}% of total`} />
        <StatCard icon={'\u26a0\ufe0f'} cls="red" label="Unverified" value={stats?.unverified ?? 0} sub="Needs admin review" />
      </div>

      <div className="admin-filter-bar">
        <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--admin-text-sec)' }}>Filter:</span>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="all">All Types</option>
          <option value="gp">GP</option>
          <option value="lp">LP</option>
          <option value="startup">Startup</option>
          <option value="advisor">Advisor</option>
        </select>
        <select value={verifiedFilter} onChange={(e) => setVerifiedFilter(e.target.value)}>
          <option value="all">All Verification</option>
          <option value="verified">Verified</option>
          <option value="pending">Pending Review</option>
          <option value="unverified">Unverified</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
        </select>
        <input type="text" placeholder="Search by company name or domain..." value={search} onChange={(e) => setSearch(e.target.value)} />
        <span className="admin-filter-spacer" />
        <button type="button" className="admin-btn admin-btn-ghost admin-btn-sm" onClick={() => { setTypeFilter('all'); setVerifiedFilter('all'); setStatusFilter('all'); setSearch('') }}>Reset</button>
      </div>

      <div className="admin-table-card">
        <table>
          <thead>
            <tr>
              <th>Company</th>
              <th>Type</th>
              <th>HQ / Region</th>
              <th>Users</th>
              <th>Verified</th>
              <th>Status</th>
              <th className="admin-th-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => {
              const tk = typeKey(c.primary_type || c.company_type)
              const vStatus = c.verification_status || 'pending'
              return (
                <tr key={c.id}>
                  <td>
                    <strong>{c.full_name || c.short_name}</strong>
                    <div style={{ fontSize: 11, color: '#909399' }}>{c.email_domain}</div>
                  </td>
                  <td><span className={`admin-type-badge ${tk}`}>{c.primary_type || c.company_type || 'GP'}</span></td>
                  <td style={{ fontSize: 12, color: 'var(--admin-text-muted)' }}>{c.hq_region || 'APAC'}</td>
                  <td style={{ fontWeight: 700 }}>{c.user_count || 0}</td>
                  <td><span className={`admin-verified-badge ${vStatus}`}>{verifiedLabel(vStatus)}</span></td>
                  <td><span className={`admin-status-badge ${c.status === 'suspended' ? 'escalated' : 'active'}`}>{c.status === 'suspended' ? 'Suspended' : 'Active'}</span></td>
                  <td className="admin-td-actions">
                    <div className="admin-action-btns">
                    <button type="button" className="admin-abtn admin-abtn-view" onClick={() => setDetailCompany(c)}>View</button>
                    {c.status === 'suspended' ? (
                      <button
                        type="button"
                        className="admin-btn admin-btn-sm admin-btn-success"
                        disabled={busyId === c.id}
                        onClick={() => unsuspendCompany(c)}
                      >
                        Unsuspend
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="admin-btn admin-btn-danger-ghost admin-btn-sm"
                        disabled={busyId === c.id}
                        onClick={() => suspendCompany(c)}
                      >
                        Suspend
                      </button>
                    )}
                    </div>
                  </td>
                </tr>
              )
            })}
            {!filtered.length && (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24, color: '#909399' }}>No company data. Run npm run db:seed:admin</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <AdminModal
        open={!!detailCompany}
        wide
        title={detailCompany?.full_name || detailCompany?.short_name || 'Company details'}
        subtitle={detailCompany?.email_domain}
        onClose={() => setDetailCompany(null)}
        footer={detailCompany ? (
          <>
            <button type="button" className="admin-btn secondary" onClick={() => setDetailCompany(null)}>Close</button>
            {detailCompany.status === 'suspended' ? (
              <button type="button" className="admin-btn admin-btn-success" disabled={busyId === detailCompany.id} onClick={() => unsuspendCompany(detailCompany)}>
                Unsuspend company
              </button>
            ) : (
              <button type="button" className="admin-btn" style={{ background: '#f56c6c', color: '#fff' }} disabled={busyId === detailCompany.id} onClick={() => suspendCompany(detailCompany)}>
                Suspend company
              </button>
            )}
          </>
        ) : null}
      >
        {detailCompany && (
          <div className="admin-review-meta-grid">
            <div className="admin-review-meta-block">
              <p><strong>Short name:</strong> {detailCompany.short_name || '—'}</p>
              <p><strong>Type:</strong> {detailCompany.primary_type || detailCompany.company_type || '—'}</p>
              <p><strong>Region:</strong> {detailCompany.hq_region || 'APAC'}</p>
            </div>
            <div className="admin-review-meta-block">
              <p><strong>Users:</strong> {detailCompany.user_count || 0}</p>
              <p><strong>Plan:</strong> {detailCompany.plan_tier || 'free'}</p>
              <p><strong>Paid:</strong> {detailCompany.is_paid ? 'Yes' : 'No'}</p>
            </div>
            <div className="admin-review-meta-block">
              <p><strong>Verification:</strong> {verifiedLabel(detailCompany.verification_status || 'pending')}</p>
              <p><strong>Max seats:</strong> {detailCompany.max_seats ?? '—'}</p>
              <p><strong>Storage limit:</strong> {detailCompany.storage_gb_limit != null ? `${detailCompany.storage_gb_limit} GB` : '—'}</p>
            </div>
            <div className="admin-review-meta-block">
              <p><strong>Created:</strong> {detailCompany.created_at ? new Date(detailCompany.created_at).toLocaleDateString() : '—'}</p>
              <p><strong>Updated:</strong> {detailCompany.updated_at ? new Date(detailCompany.updated_at).toLocaleDateString() : '—'}</p>
              <p><strong>Company ID:</strong> <span style={{ fontSize: 11, wordBreak: 'break-all' }}>{detailCompany.id}</span></p>
            </div>
          </div>
        )}
      </AdminModal>
    </AdminLayout>
  )
}
